import { DatePipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  input,
  inject,
} from '@angular/core';
import { CarouselModule } from 'primeng/carousel';
import { Chart } from 'chart.js/auto';
import { SkeletonModule } from 'primeng/skeleton';
import {
  ClientService,
  TimeconversionService,
  ToastMessageService,
} from '@fitness-force/api';
import { TENANTS_INFO } from '@fitness-force/state';
import { Store } from '@ngrx/store';
import type {
  FitnessData,
  TENANTSINTERFACE,
  WeekData,
} from '@fitness-force/models';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'client-fitness-tab',
  templateUrl: './client-fitness-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CarouselModule, SkeletonModule, TranslateModule, UpperCasePipe],
})
export class ClientFitnessTabComponent implements OnInit {
  cdr = inject(ChangeDetectorRef);
  timeService = inject(TimeconversionService);
  clientService = inject(ClientService);
  private store = inject(Store);
  toastMessageService = inject(ToastMessageService);
  private datepipe = inject(DatePipe);

  readonly client_uuid = input<string>('');
  tenantId: number = 0;
  weeks: WeekData[] = [];
  allData: WeekData[] = [];
  wellbeing1: number = 0;
  showSkeleton: boolean = true;
  responsiveOptions: any[];

  fitnessData: FitnessData[] = [];
  biomarkers: FitnessData['factors'][0][] = [];
  activityChart: any;
  activityBarChart: any;
  wellbeingChart: any;
  selectedDate: Date;
  distinctMetrics: { type: string; score: number; state: string }[] = [];

  whiteListedBiomarkers: string[] = [
    'steps',
    'active_hours',
    'active_calories',
    'extended_inactivity',
    'sleep_duration',
    'sleep_debt',
    'sleep_regularity',
    'physical_recovery',
    'mental_recovery',
    'activity_regularity',
    'floors_climbed',
    'intense_activity_duration',
  ];
  startDate: Date;
  endDate: Date;

  constructor() {
    this.responsiveOptions = [
      {
        breakpoint: '1024px',
        numVisible: 5,
        numScroll: 1,
      },
      {
        breakpoint: '768px',
        numVisible: 3,
        numScroll: 1,
      },
      {
        breakpoint: '560px',
        numVisible: 2,
        numScroll: 1,
      },
    ];
  }

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      if (this.tenantId > 0) {
        this.store.dispatch({ type: 'ROUTER_GO' });
        this.loadFitnessData();
      }
    });
  }

  private loadFitnessData(): void {
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() - 8);
    const payload = {
      client_uuid: this.client_uuid() || 'E53EB2D2-CD73-4468-9171-6E227E61F43F',
      start_date_time: this.datepipe.transform(
        endDate.toISOString(),
        'yyyy-MM-dd',
      ),
      end_date_time: this.datepipe.transform(
        startDate.toISOString(),
        'yyyy-MM-dd',
      ),
      health_score_request_type: [
        'activity',
        'sleep',
        'mental_wellbeing',
        'wellbeing',
      ],
    };

    this.clientService.getClientFitnessData(this.tenantId, payload).subscribe({
      next: (res: any) => {
        this.fitnessData = res;
        this.initializeView();
      },
      error: (error: any) => {
        this.toastMessageService.showError(error);
      },
      complete: () => {
        this.showSkeleton = false;
      },
    });
  }

  private initializeView(): void {
    // Get unique dates from fitnessData
    const today = new Date();
    const last9Days: Date[] = [];
    for (let i = 0; i < 10; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      last9Days.push(date);
    }
    last9Days.reverse();
    const uniqueDates = last9Days;

    // const uniqueDates = Array.from(
    //   new Set(
    //     this.fitnessData.map((data) =>
    //       this.timeService.ConvertUTCToLocalDateTime(data.scoreDateTime)
    //     )
    //   )
    // ).sort((a, b) => a.getTime() - b.getTime());

    // Get distinct dates by removing time component
    const distinctDates = uniqueDates
      .map(
        (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()),
      )
      .filter(
        (date, index, self) =>
          self.findIndex((d) => d.getTime() === date.getTime()) === index,
      );

    this.selectedDate = distinctDates[distinctDates.length - 1];

    // Initialize weeks data with unique dates
    this.initializeWeekData(distinctDates);

    // Update metrics for selected date
    this.updateMetricsForDate(this.selectedDate);
    this.updateCharts();

    setTimeout(() => {
      this.showSkeleton = false;
      this.cdr.detectChanges();
    }, 1000);
    this.cdr.detectChanges();
  }

  private initializeWeekData(uniqueDates: Date[]): void {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var fitnessData = this.fitnessData.filter((d) => d.type === 'activity');
    var data = uniqueDates.map((date) => {
      const selectedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      const activityData = fitnessData.find((d) => {
        const dataDate = new Date(d.scoreDateTime);
        return (
          dataDate.getFullYear() === selectedDate.getFullYear() &&
          dataDate.getMonth() === selectedDate.getMonth() &&
          dataDate.getDate() === selectedDate.getDate() &&
          d.type === 'activity'
        );
      });

      return {
        dayName: days[date.getDay()],
        month: date.getMonth() + 1,
        day: date.getDate().toString(),
        date: date,
        scoreDateTime: date.toString(),
        activity: activityData
          ? `${Math.round(activityData.score * 100)}`
          : '-',
        selected: date === this.selectedDate,
      };
    });
    this.allData = data;
    this.weeks = data.slice(3, 10);
  }

  selectDate(date: Date): void {
    this.selectedDate = date;
    this.weeks = this.weeks.map((week) => ({
      ...week,
      selected: week.date === date,
    }));
    this.updateMetricsForDate(date);
    // Update all charts with the new selected date
    this.createActivityChart();
    this.createWellbeingChart();
    this.cdr.detectChanges();
  }

  private updateMetricsForDate(date: Date): void {
    const selectedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const dateData = this.fitnessData.filter((d) => {
      const dataDate = new Date(d.scoreDateTime);
      return (
        dataDate.getFullYear() === selectedDate.getFullYear() &&
        dataDate.getMonth() === selectedDate.getMonth() &&
        dataDate.getDate() === selectedDate.getDate()
      );
    });

    // Get distinct metrics for the selected date
    this.distinctMetrics = [
      {
        type: 'activity',
        score: this.getScoreForType(dateData, 'activity'),
        state: dateData.find((d) => d.type === 'activity')?.state || '',
      },
      {
        type: 'sleep',
        score: this.getScoreForType(dateData, 'sleep'),
        state: dateData.find((d) => d.type === 'sleep')?.state || '',
      },
      {
        type: 'mental_wellbeing',
        score: this.getScoreForType(dateData, 'mental_wellbeing') || 0,
        state: dateData.find((d) => d.type === 'mental_wellbeing')?.state || '',
      },
      {
        type: 'wellbeing',
        score: this.getScoreForType(dateData, 'wellbeing') || 0,
        state: dateData.find((d) => d.type === 'wellbeing')?.state || '',
      },
    ];

    // Get all biomarkers for the selected date
    const allBiomarkers = dateData.reduce(
      (acc, data) => {
        return [...acc, ...data.factors];
      },
      [] as FitnessData['factors'][0][],
    );

    // Filter only whitelisted biomarkers and maintain their order
    this.biomarkers = this.whiteListedBiomarkers
      .map((name) => allBiomarkers.find((biomarker) => biomarker.name === name))
      .filter(
        (biomarker): biomarker is FitnessData['factors'][0] =>
          biomarker !== undefined,
      );
  }

  private getScoreForType(data: FitnessData[], type: string): number {
    const metric = data.find((d) => d.type === type);
    return metric ? Math.round(metric.score * 100) : 0;
  }

  createActivityChart(
    type: 'activity' | 'sleep' | 'mental_wellbeing' = 'activity',
  ): void {
    var activityData = this.fitnessData
      .filter((d) => d.type === type)
      .reverse();

    const ctx = document.getElementById('activityChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.activityChart) {
      this.activityChart.destroy();
    }

    this.activityChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: activityData.map((d) =>
          this.timeService
            .ConvertUTCToLocalDateTime(d.scoreDateTime)
            .toLocaleDateString(),
        ),
        datasets: [
          {
            // label: type === 'activity' ? 'Activity Score' : type === 'sleep' ? 'Sleep Score' : 'Mental Score',
            data: activityData.map((d) => d.score * 100),
            backgroundColor:
              type === 'activity'
                ? '#00BCD4'
                : type === 'sleep'
                  ? '#9C27B0'
                  : '#E91E63',
            borderColor:
              type === 'activity'
                ? '#00BCD4'
                : type === 'sleep'
                  ? '#9C27B0'
                  : '#E91E63',
            borderWidth: 1,
            barThickness: 'flex',
          },
        ],
      },
      options: {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
      },
    });
  }

  private createActivityBarChart(): void {
    const ctx = document.getElementById(
      'activityBarChart',
    ) as HTMLCanvasElement;
    if (!ctx) return;

    // Get data for the selected date
    const selectedDateData = this.fitnessData.filter(
      (d) =>
        this.timeService
          .ConvertUTCToLocalDateTime(d.scoreDateTime)
          .getDate() === this.selectedDate.getDate(),
    );

    // Prepare data for the chart
    const metrics = [
      { type: 'activity', label: 'Activity', color: '#00BCD4' },
      { type: 'sleep', label: 'Sleep', color: '#9C27B0' },
      { type: 'mental_wellbeing', label: 'Mental', color: '#E91E63' },
    ];

    const data = metrics.map((metric) => {
      if (metric.type === 'mental_wellbeing') {
        return 90; // Placeholder for mental wellbeing
      }
      const record = selectedDateData.find((d) => d.type === metric.type);
      return record ? Math.round(record.score * 100) : 0;
    });

    if (this.activityBarChart) {
      this.activityBarChart.destroy();
    }

    this.activityBarChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: metrics.map((m) => m.label),
        datasets: [
          {
            data: data,
            backgroundColor: metrics.map((m) => m.color),
            borderRadius: 4,
            borderSkipped: false,
            barThickness: 30,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
          },
          x: {
            beginAtZero: true,
            max: 100,
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
            ticks: {
              display: true,
              stepSize: 20,
            },
          },
        },
      },
    });
  }

  private createWellbeingChart(): void {
    const ctx = document.getElementById('wellbeingChart') as HTMLCanvasElement;
    if (!ctx) return;
    // Destroy existing chart if it exists
    if (this.wellbeingChart) {
      this.wellbeingChart.destroy();
    }

    const wellbeingScore =
      this.distinctMetrics.find((m) => m.type === 'wellbeing')?.score || 0;
    const wellbeingState =
      this.distinctMetrics.find((m) => m.type === 'wellbeing')?.state || '';

    this.wellbeingChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        datasets: [
          {
            data: [wellbeingScore, 100 - wellbeingScore],
            backgroundColor: ['#3B82F6', '#E0E0E0'],
            borderWidth: 0,
            circumference: 180,
            rotation: 270,
          },
        ],
        labels: [''],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
          },
        },
      },
      plugins: [
        {
          id: 'wellbeingText',
          afterDraw(chart: any) {
            const {
              ctx,
              chartArea: { width, height },
            } = chart;
            ctx.save();

            // Calculate center point
            const centerX = width / 2;
            const centerY = height / 2;
            const radius = Math.min(width, height) / 2.5;

            // Draw the arc text (state)
            ctx.font = '16px Arial';
            ctx.fillStyle = '#3B82F6';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Calculate position for the arc text
            const angle = -Math.PI / 2; // Start at top (-90 degrees)
            const x = centerX + radius * Math.cos(angle);
            const y = centerY + radius * Math.sin(angle) - 20; // Offset by 20px up
            ctx.fillText(wellbeingState.toUpperCase(), x, y);

            // Draw the score
            ctx.font = 'bold 32px Arial';
            ctx.fillStyle = '#3B82F6';
            ctx.fontWeight = 'bold';
            ctx.fillText(wellbeingScore.toString(), centerX, centerY + 30);

            // Draw the label based on score
            ctx.font = 'bold 16px Arial';
            ctx.fillStyle = '#666';
            const label = wellbeingState.toUpperCase();
            ctx.fillText(label, centerX, centerY + 60);

            ctx.restore();
          },
        },
      ],
    });
  }

  formatValue(factor: any): string {
    switch (factor.unit) {
      case 'count':
        return factor.value ? factor.value.toLocaleString() : '-';
      case 'hour':
        return factor.value ? `${factor.value} HRS` : '-';
      case 'minute':
        return factor.value ? `${factor.value} MINS` : '-';
      case 'index':
        return factor.value ? `${(factor.value * 100).toFixed(0)}%` : '-';
      case 'kcal':
        return factor.value ? `${factor.value} KCAL` : '-';
      default:
        return factor.value ? factor.value.toString() : '-';
    }
  }

  // Method to update bar chart data based on selected metric
  updateBarChartData(metric: 'activity' | 'sleep' | 'mental_wellbeing'): void {
    // No longer needed as we're showing all metrics at once
    this.createActivityBarChart();
  }

  private getMonthlyDataForMetric(metric: string): number[] {
    const monthlyData = new Array(12).fill(0);

    this.fitnessData
      .filter((d) => d.type === metric)
      .forEach((d) => {
        const month = new Date(d.scoreDateTime).getMonth();
        monthlyData[month] = Math.round(d.score * 100);
      });

    return monthlyData;
  }

  private updateCharts(): void {
    this.createActivityChart();
    this.createActivityBarChart();
    this.createWellbeingChart();
  }
}
